/**
 * ra-multi-buy.js
 * Rod Armour — Multi-Buy Offer block JS
 *
 * Responsibilities:
 *  1. Watch the product qty stepper and update the progress text in real time.
 *  2. Highlight the active discount tier as qty changes.
 *  3. Multi-size selector: sum rows, update total counter, handle ATC.
 *
 * Discount application is handled by the Rebuy app — this file is display-only.
 */

(function () {
  'use strict';

  // ─── Global listener controller (prevents duplicate listeners on section reload) ─
  let _listenerController = null;

  // ─── Helpers ───────────────────────────────────────────────────────────────

  function int(val) {
    return parseInt(val, 10) || 0;
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }

    const format = window.theme && theme.settings && theme.settings.moneyFormat
      ? theme.settings.moneyFormat
      : '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const parsed = Number(number);
      if (isNaN(parsed) || parsed == null) return '0';
      const amount = (parsed / 100.0).toFixed(precision);
      const parts = amount.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const fraction = parts[1] ? decimal + parts[1] : '';
      return whole + fraction;
    }

    let value = '';
    switch (placeholder) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
    }

    return format.replace(placeholderRegex, value);
  }

  function parseCartAddResponse(res) {
    return res.text().then(function (text) {
      const trimmed = text ? text.trim() : '';

      if (!res.ok) {
        throw new Error('Cart error ' + res.status + (trimmed ? ': ' + trimmed.slice(0, 120) : ''));
      }

      if (!trimmed) {
        return {};
      }

      try {
        return JSON.parse(trimmed);
      } catch (err) {
        throw new Error('Cart add returned non-JSON response');
      }
    });
  }

  function updateTierPricing(block, basePrice) {
    const priceSuffix = block.getAttribute('data-price-suffix') || 'ea';
    const tierEls = block.querySelectorAll('[data-tier][data-tier-pct]');

    tierEls.forEach(function (tierEl) {
      const tierPct = int(tierEl.getAttribute('data-tier-pct'));
      const multiplier = 100 - tierPct;
      const discountedUnit = Math.round((basePrice * multiplier) / 100);

      const compareEl = tierEl.querySelector('[data-tier-compare]');
      const priceEl = tierEl.querySelector('[data-tier-price]');
      const suffixEl = tierEl.querySelector('.ra-multi-buy__tier-price-suffix');

      if (compareEl) compareEl.textContent = formatMoney(basePrice);
      if (priceEl) priceEl.textContent = formatMoney(discountedUnit);
      if (suffixEl) suffixEl.textContent = priceSuffix;
    });
  }

  // ─── Progress text helpers ─────────────────────────────────────────────────

  function buildTieredProgressText(qty, tiers, t) {
    // tiers is sorted ascending by qty
    let activeTier = null;
    let nextTier = null;

    for (let i = 0; i < tiers.length; i++) {
      if (qty >= tiers[i].qty) {
        activeTier = tiers[i];
      } else {
        if (!nextTier) nextTier = tiers[i];
      }
    }

    if (activeTier && !nextTier) {
      return t.progressMax.replace('__PCT__', activeTier.pct);
    } else if (activeTier && nextTier) {
      const need = nextTier.qty - qty;
      return t.progressNext.replace('__NEED__', need).replace('__PCT__', nextTier.pct);
    } else if (nextTier) {
      const need = nextTier.qty - qty;
      return t.progressUnlock.replace('__NEED__', need).replace('__PCT__', nextTier.pct);
    }
    return '';
  }

  function buildBxgyProgressText(qty, buy, get, t) {
    if (qty >= buy) {
      return (get > 1 ? t.bxgyUnlockedPlural : t.bxgyUnlocked).replace('__COUNT__', get);
    }
    const need = buy - qty;
    return t.bxgyNeed.replace('__NEED__', need).replace('__COUNT__', get);
  }

  // ─── bxgy_mixer helpers ────────────────────────────────────────────────────

  function pluralSleeves(n) {
    return n === 1 ? 'sleeve' : 'sleeves';
  }

  function buildMixerState(total, mx) {
    // Returns {tierReached: 0|1|2, fillPct, progressText, btnLabel}
    const t1Total = mx.t1Buy + mx.t1Get;
    const t2Total = mx.t2Buy + mx.t2Get;

    let tierReached = 0;
    if (mx.t2Enabled && total >= t2Total) tierReached = 2;
    else if (total >= t1Total) tierReached = 1;

    // Progress bar fill — anchored to the next unreached tier (or maxed out)
    let fillPct = 0;
    if (tierReached === 0) {
      fillPct = Math.min(100, Math.round((total / t1Total) * 100));
    } else if (tierReached === 1 && mx.t2Enabled) {
      fillPct = Math.min(100, Math.round((total / t2Total) * 100));
    } else {
      fillPct = 100;
    }

    // Progress message — tidied: clearer empty state, no "discount applies at checkout" filler
    let progressText;
    if (total === 0) {
      progressText = 'Pick ' + t1Total + ' - ' + mx.t1Get + ' free';
    } else if (tierReached === 0) {
      const need = t1Total - total;
      progressText = need + ' more to unlock free ' + pluralSleeves(mx.t1Get);
    } else if (tierReached === 1 && mx.t2Enabled) {
      const need = t2Total - total;
      progressText = 'Free sleeve unlocked · ' + need + ' more for ' + mx.t2Get + ' FREE';
    } else if (tierReached === 1) {
      progressText = 'Free sleeve unlocked';
    } else {
      progressText = 'Max savings unlocked · ' + mx.t2Get + ' FREE sleeves inside';
    }

    // Button label — uses "FREE SLEEVE INSIDE" copy at unlock
    let btnLabel;
    if (total === 0) {
      btnLabel = 'Add to Cart';
    } else if (tierReached === 0) {
      btnLabel = 'Add ' + total + ' to Cart';
    } else if (tierReached === 1) {
      btnLabel = 'Add ' + total + ' to Cart · FREE SLEEVE INSIDE';
    } else {
      btnLabel = 'Add ' + total + ' to Cart · ' + mx.t2Get + ' FREE INSIDE';
    }

    return { tierReached: tierReached, fillPct: fillPct, progressText: progressText, btnLabel: btnLabel };
  }

  function applyMixerVisuals(block, state) {
    // Tier cards highlight
    const tierEls = block.querySelectorAll('[data-mixer-tier]');
    tierEls.forEach(function (el) {
      const tn = int(el.getAttribute('data-mixer-tier'));
      if (tn <= state.tierReached) {
        el.classList.add('ra-multi-buy__mixer-tier--reached');
      } else {
        el.classList.remove('ra-multi-buy__mixer-tier--reached');
      }
    });

    // Progress bar fill
    const fillEl = block.querySelector('[data-mixer-progress-fill]');
    if (fillEl) fillEl.style.width = state.fillPct + '%';

    // Progress text
    const progressEl = block.querySelector('[data-multi-buy-progress]');
    if (progressEl) progressEl.textContent = state.progressText;

    // Button label + state class
    const btnLabelEl = block.querySelector('[data-mixer-btn-label]');
    if (btnLabelEl) btnLabelEl.textContent = state.btnLabel;

    const atcBtn = block.querySelector('[data-multi-size-atc]');
    if (atcBtn) {
      if (state.tierReached > 0) {
        atcBtn.classList.add('is-tier-reached');
      } else {
        atcBtn.classList.remove('is-tier-reached');
      }
    }
  }

  // ─── Tier highlight ────────────────────────────────────────────────────────

  function updateActiveTier(block, qty) {
    const tierEls = block.querySelectorAll('[data-tier]');
    let activeTierQty = 0;

    tierEls.forEach(function (el) {
      const tierQty = int(el.getAttribute('data-tier'));
      if (qty >= tierQty && tierQty > activeTierQty) {
        activeTierQty = tierQty;
      }
    });

    tierEls.forEach(function (el) {
      const tierQty = int(el.getAttribute('data-tier'));
      if (tierQty === activeTierQty && activeTierQty > 0) {
        el.classList.add('ra-multi-buy__tier--active');
      } else {
        el.classList.remove('ra-multi-buy__tier--active');
      }
    });
  }

  // ─── Main init per block ───────────────────────────────────────────────────

  function initBlock(block) {
    const offerType = block.getAttribute('data-offer-type');
    let basePrice = int(block.getAttribute('data-base-price'));

    // Translation strings from data attributes (set by Liquid | t)
    const t = {
      progressMax:        block.dataset.tProgressMax        || "You're saving __PCT__% — maximum discount applied!",
      progressNext:       block.dataset.tProgressNext       || 'Add __NEED__ more to save __PCT__%',
      progressUnlock:     block.dataset.tProgressUnlock     || 'Add __NEED__ more to unlock __PCT__% off',
      bxgyUnlocked:       block.dataset.tBxgyUnlocked       || '__COUNT__ free unlocked',
      bxgyUnlockedPlural: block.dataset.tBxgyUnlockedPlural || '__COUNT__ free unlocked',
      bxgyNeed:           block.dataset.tBxgyNeed            || 'Add __NEED__ for __COUNT__ free',
      btnAdding:          block.dataset.tBtnAdding           || 'Adding\u2026',
      btnAdded:           block.dataset.tBtnAdded            || 'Added!',
      btnAddAll:          block.dataset.tBtnAddAll           || 'Add All to Cart',
      btnError:           block.dataset.tBtnError            || 'Error \u2014 try again',
    };

    const progressEl = block.querySelector('[data-multi-buy-progress]');

    // Build tiers array for tiered_percent
    const tiers = [];
    if (offerType === 'tiered_percent') {
      const t1q = int(block.getAttribute('data-tier1-qty'));
      const t1p = int(block.getAttribute('data-tier1-pct'));
      const t2q = int(block.getAttribute('data-tier2-qty'));
      const t2p = int(block.getAttribute('data-tier2-pct'));
      const t3q = int(block.getAttribute('data-tier3-qty'));
      const t3p = int(block.getAttribute('data-tier3-pct'));

      if (t1q > 0) tiers.push({ qty: t1q, pct: t1p });
      if (t2q > 0) tiers.push({ qty: t2q, pct: t2p });
      if (t3q > 0) tiers.push({ qty: t3q, pct: t3p });

      // Sort ascending
      tiers.sort(function (a, b) { return a.qty - b.qty; });
    }

    const bxgyBuy = int(block.getAttribute('data-bxgy-buy'));
    const bxgyGet = int(block.getAttribute('data-bxgy-get'));

    // bxgy_mixer config
    const mixer = {
      t1Buy:     int(block.getAttribute('data-mixer-t1-buy')),
      t1Get:     int(block.getAttribute('data-mixer-t1-get')),
      t2Enabled: block.getAttribute('data-mixer-t2-enabled') === '1',
      t2Buy:     int(block.getAttribute('data-mixer-t2-buy')),
      t2Get:     int(block.getAttribute('data-mixer-t2-get')),
    };
    block._raMixer = mixer; // expose for the multi-size selector update loop

    // ── Listen to qty changes ──
    // The theme's qty stepper updates the hidden input [name="quantity"] on the form.
    // We watch for input/change on that element, and also hook into the stepper buttons.

    function getQty() {
      // Prefer the multi-size total if visible
      const multiSizeEl = block.querySelector('[data-multi-size-total]');
      if (multiSizeEl && multiSizeEl.closest('[data-multi-size-selector]') && !multiSizeEl.closest('[data-multi-size-selector]').hidden) {
        return int(multiSizeEl.textContent);
      }

      // Otherwise read the standard qty input in the product form
      let formQtyInput = document.querySelector('[data-product-blocks] input[name="quantity"]');
      if (!formQtyInput) {
        formQtyInput = document.querySelector('input.js-qty__num');
      }
      return formQtyInput ? int(formQtyInput.value) : 1;
    }

    function refresh() {
      const qty = getQty();
      if (progressEl) {
        if (offerType === 'tiered_percent') {
          progressEl.textContent = buildTieredProgressText(qty, tiers, t);
        } else if (offerType === 'bxgy') {
          progressEl.textContent = buildBxgyProgressText(qty, bxgyBuy, bxgyGet, t);
        } else if (offerType === 'bxgy_mixer') {
          // bxgy_mixer is driven by the multi-size selector total, not the qty stepper.
          // applyMixerVisuals is called from updateTotal() in initMultiSizeSelector.
          // Initial paint here in case the stepper fires first.
          applyMixerVisuals(block, buildMixerState(0, mixer));
        }
      }
      if (offerType === 'tiered_percent') {
        updateTierPricing(block, basePrice);
        updateActiveTier(block, qty);
      }
    }

    // ── Clean up previous global listeners before re-attaching ──
    // Prevents duplicate handlers when Shopify re-renders the section in the editor.
    if (_listenerController) _listenerController.abort();
    _listenerController = new AbortController();
    const signal = _listenerController.signal;

    // Observe quantity input changes globally (standard stepper)
    document.addEventListener('change', function (e) {
      if (e.target && (e.target.name === 'quantity' || e.target.classList.contains('js-qty__num'))) {
        refresh();
      }
    }, { signal: signal });

    // Also catch +/– button clicks which may not fire change on the input
    document.addEventListener('click', function (e) {
      if (e.target && e.target.classList.contains('js-qty__adjust')) {
        // Small delay so the input value updates first
        setTimeout(refresh, 50);
      }
    }, { signal: signal });

    document.addEventListener('variant:change', function (e) {
      if (!e.detail || !e.detail.variant || !e.detail.variant.price) return;
      basePrice = int(e.detail.variant.price);
      block.setAttribute('data-base-price', basePrice);
      refresh();
    }, { signal: signal });

    // ── Tier click → set qty stepper ──
    block.addEventListener('click', function (e) {
      const tierEl = e.target.closest('[data-tier-select]');
      if (!tierEl) return;
      const targetQty = parseInt(tierEl.getAttribute('data-tier-select'), 10);
      if (!targetQty) return;
      const qtyInput = document.querySelector('[data-product-blocks] input[name="quantity"]')
                  || document.querySelector('input.js-qty__num');
      if (qtyInput) {
        qtyInput.value = targetQty;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Keyboard support (Enter / Space) for tier cards
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const tierEl = e.target.closest('[data-tier-select]');
      if (!tierEl) return;
      e.preventDefault();
      tierEl.click();
    }, { signal: signal });

    // Initial render
    refresh();

    // ── Multi-size selector ──
    initMultiSizeSelector(block, refresh, t);
  }

  // ─── Multi-size selector ───────────────────────────────────────────────────

  function initMultiSizeSelector(block, refreshProgress, t) {
    const selectorEl = block.querySelector('[data-multi-size-selector]');
    if (!selectorEl) return;

    const totalEl = block.querySelector('[data-multi-size-total]');
    const atcBtn = block.querySelector('[data-multi-size-atc]');

    const offerType = block.getAttribute('data-offer-type');
    const mixer = block._raMixer;

    function updateTotal() {
      const rows = selectorEl.querySelectorAll('[data-size-row]');
      let total = 0;
      rows.forEach(function (row) {
        total += int(row.querySelector('.ra-multi-buy__size-qty').value);
      });
      if (totalEl) totalEl.textContent = total;
      if (atcBtn) atcBtn.disabled = total === 0;

      // bxgy_mixer: apply the full visual state (tier highlights, progress bar, button label)
      if (offerType === 'bxgy_mixer' && mixer) {
        applyMixerVisuals(block, buildMixerState(total, mixer));
      } else if (refreshProgress) {
        refreshProgress();
      }
    }

    // Delegate +/– clicks and input changes inside multi-size selector
    selectorEl.addEventListener('click', function (e) {
      const target = e.target;

      if (target.classList.contains('ra-multi-buy__size-minus') || target.classList.contains('ra-multi-buy__size-plus')) {
        const row = target.closest('[data-size-row]');
        if (!row) return;
        const input = row.querySelector('.ra-multi-buy__size-qty');
        const current = int(input.value);
        if (target.classList.contains('ra-multi-buy__size-minus')) {
          input.value = Math.max(0, current - 1);
        } else {
          input.value = current + 1;
        }
        updateTotal();
      }

      // Multi-ATC button
      if (target === atcBtn || atcBtn.contains(target)) {
        submitMultiSizeCart(block, atcBtn, t);
      }
    });

    selectorEl.addEventListener('input', function (e) {
      if (e.target.classList.contains('ra-multi-buy__size-qty')) {
        updateTotal();
      }
    });

    // Initial state
    updateTotal();
  }

  // ─── Multi-size cart submission ────────────────────────────────────────────

  function submitMultiSizeCart(block, atcBtn, t) {
    const rows = block.querySelectorAll('[data-size-row]');
    const items = [];

    rows.forEach(function (row) {
      const qty = int(row.querySelector('.ra-multi-buy__size-qty').value);
      const variantId = int(row.getAttribute('data-variant-id'));
      if (qty > 0 && variantId) {
        items.push({ id: variantId, quantity: qty });
      }
    });

    if (items.length === 0) return;

    // bxgy_mixer mode preserves the inner label span; other modes use textContent.
    const labelEl = atcBtn.querySelector('[data-mixer-btn-label]');
    const setBtnText = function (txt) {
      if (labelEl) labelEl.textContent = txt;
      else atcBtn.textContent = txt;
    };

    atcBtn.disabled = true;
    setBtnText(t.btnAdding);

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: items }),
      signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10000) : undefined
    })
      .then(parseCartAddResponse)
      .then(function () {
        // Trigger the theme's cart update event so the drawer / header qty refreshes
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('ra:cart:open', {
          bubbles: true,
          detail: { source: 'ra-multi-buy' }
        }));
        // Reset rows
        block.querySelectorAll('.ra-multi-buy__size-qty').forEach(function (input) {
          input.value = 0;
        });
        const totalEl = block.querySelector('[data-multi-size-total]');
        if (totalEl) totalEl.textContent = '0';
        setBtnText(t.btnAdded);
        setTimeout(function () {
          setBtnText(labelEl ? 'Add to Cart' : t.btnAddAll);
          atcBtn.disabled = true;
          // Repaint mixer visuals (clears tier highlights / progress bar)
          if (block.getAttribute('data-offer-type') === 'bxgy_mixer' && block._raMixer) {
            applyMixerVisuals(block, buildMixerState(0, block._raMixer));
          }
        }, 2000);
      })
      .catch(function (err) {
        // Surface errors in theme editor only — silent on live store
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('ra-multi-buy ATC error:', err);
        }
        setBtnText(t.btnError);
        atcBtn.disabled = false;
        setTimeout(function () {
          setBtnText(labelEl ? 'Add to Cart' : t.btnAddAll);
        }, 3000);
      });
  }

  // ─── Collapsible header (bxgy_mixer / bxgy_simple) ─────────────────────────

  function initCollapsible(block) {
    if (!block.hasAttribute('data-collapsible')) return;
    const toggle = block.querySelector('[data-collapse-toggle]');
    if (!toggle) return;

    toggle.addEventListener('click', function () {
      const isCollapsed = block.getAttribute('data-collapsed') !== 'false';
      const next = isCollapsed ? 'false' : 'true';
      block.setAttribute('data-collapsed', next);
      toggle.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
    });
  }

  // ─── bxgy_simple — single-button ATC ───────────────────────────────────────

  function initSimpleAtc(block) {
    const btn = block.querySelector('[data-simple-atc]');
    if (!btn) return;
    const labelEl = btn.querySelector('[data-simple-btn-label]');
    const originalLabel = labelEl ? labelEl.textContent : btn.textContent;

    function setLabel(txt) {
      if (labelEl) labelEl.textContent = txt;
      else btn.textContent = txt;
    }

    btn.addEventListener('click', function () {
      const variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
      const qty = parseInt(btn.getAttribute('data-add-qty'), 10);
      if (!variantId || !qty) return;

      btn.disabled = true;
      setLabel('Adding\u2026');

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] }),
        signal: typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(10000) : undefined
      })
        .then(parseCartAddResponse)
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('ra:cart:open', {
            bubbles: true,
            detail: { source: 'ra-multi-buy-simple' }
          }));
          setLabel('Added!');
          setTimeout(function () {
            setLabel(originalLabel);
            btn.disabled = false;
          }, 2000);
        })
        .catch(function (err) {
          if (window.Shopify && window.Shopify.designMode) {
            console.warn('ra-multi-buy simple ATC error:', err);
          }
          setLabel('Error \u2014 try again');
          setTimeout(function () {
            setLabel(originalLabel);
            btn.disabled = false;
          }, 3000);
        });
    });
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────

  const initializedBlocks = new WeakSet();
  let blockObserver = null;

  function bootBlock(block) {
    if (!block || initializedBlocks.has(block)) return;
    initializedBlocks.add(block);
    initBlock(block);
    initCollapsible(block);
    initSimpleAtc(block);
  }

  function ensureObserver() {
    if (blockObserver || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      return;
    }

    blockObserver = new IntersectionObserver(function(entries, observer) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        bootBlock(entry.target);
      });
    }, {
      rootMargin: '300px 0px'
    });
  }

  function init(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const blocks = root.querySelectorAll('[data-multi-buy]');
    if (!blocks.length) return;

    ensureObserver();

    blocks.forEach(function (block) {
      if (initializedBlocks.has(block)) return;

      if (!blockObserver) {
        bootBlock(block);
        return;
      }

      blockObserver.observe(block);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section/block re-render (theme editor live preview)
  document.addEventListener('shopify:section:load', function(event) {
    init(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function() {
    /* Cleanup to prevent memory leaks in theme editor */
  });
  document.addEventListener('shopify:block:select', function(event) {
    init(event.target || document);
  });

})();
